import { jwtDecode } from "jwt-decode";

import type { AuthStrategy, SandboxApiError } from "sandbox/types";

import { SANDBOX_API_BASE, STORAGE_KEYS } from "sandbox/constants";
import { sandboxPaths } from "sandbox/paths";
import { resetSandboxState } from "sandbox/utils/resetState";

const TOKEN_KEY = `${STORAGE_KEYS.user}-token`;

export const storeToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {}
};

export const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const clearToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
};

const logoutAndRedirect = (): void => {
  clearToken();
  resetSandboxState();
  globalThis.location.href = sandboxPaths.login;
};

const checkTokenExpiry = (): void => {
  const token = getStoredToken();
  if (!token) return;

  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (exp && exp * 1000 < Date.now()) {
      logoutAndRedirect();
    }
  } catch {
    logoutAndRedirect();
  }
};

const buildUrl = (path: string): string => {
  const base = SANDBOX_API_BASE.replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
};

const buildError = (
  message: string,
  status: number,
  body: unknown,
): SandboxApiError => ({ message, status, body });

export const sandboxFetch = async (
  path: string,
  {
    method,
    body,
    headers: extraHeaders,
    auth,
    authToken,
  }: {
    method: string;
    body?: unknown;
    headers?: Record<string, string>;
    auth: AuthStrategy;
    authToken?: string;
  },
): Promise<Response> => {
  checkTokenExpiry();

  const url = buildUrl(path);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...extraHeaders,
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  } else if (auth.mode === "authenticated") {
    const token = getStoredToken();
    if (!token) {
      throw buildError(
        "No authentication token found. Please sign in.",
        401,
        null,
      );
    }
    headers.Authorization = `Bearer ${token}`;
  }

  const init: RequestInit = { method, headers };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    throw buildError(
      error instanceof Error ? error.message : "Network error",
      0,
      null,
    );
  }

  if (response.status === 401) {
    clearToken();
  }

  return response;
};
